// 2D architectural floorplan, ported from iOS FloorplanView.swift + PlanDimensions.swift.
// Renders a responsive SVG: paper bg, floor fill, thickened double-line walls with REAL
// door/window/opening gaps, opening symbols (door swing arc, window 3-line, dashed
// opening), furniture footprints + labels, stair symbol, and per-segment dimension
// chains with collision avoidance. Pinch/scroll zoom + drag pan over a fit-to-view plan.

import { useMemo, useRef, useState } from "react";
import type {
  ObjectFootprint,
  ScanGeometry,
  Vec2,
  WallDecomposition,
  WallOpeningSlot,
} from "../geometry/types";
import { squareUpRotation } from "../geometry/scanGeometry";
import { PlanProjection, type Point } from "../geometry/projection";
import { constructionLength, type MeasurementSystem } from "../geometry/format";

// Theme colors (mirror tailwind.config / Theme.swift).
const INK = "#2B2522";
const ACCENT = "#CA6B4A";
const PAPER = "#FBF7F2";

const VIEW_W = 1000;
const VIEW_H = 760;
const PADDING = 56;

// MARK: - dimension drawing (port of PlanDimensions.draw)

interface DimStyle {
  segmentOffset: number;
  rowPitch: number;
  tickLength: number;
  extOvershoot: number;
  extGap: number;
  labelLift: number;
  fontSize: number;
}

const DIM_STYLE: DimStyle = {
  segmentOffset: 22,
  rowPitch: 18,
  tickLength: 6,
  extOvershoot: 5,
  extGap: 3,
  labelLift: 8,
  fontSize: 9.5,
};

interface SvgRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const rectsIntersect = (a: SvgRect, b: SvgRect): boolean =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

interface DimLabel {
  text: string;
  cx: number;
  cy: number;
  angleDeg: number;
}

interface DimPrimitives {
  lines: { x1: number; y1: number; x2: number; y2: number; opacity: number; width: number }[];
  ticks: { x1: number; y1: number; x2: number; y2: number }[];
  labels: DimLabel[];
}

/**
 * Builds the dimension package for one wall in SCREEN space (correct for any
 * projection, including a rotated plan). Mutates `occupied` for collision avoidance.
 * Faithful to iOS PlanDimensions.draw.
 */
function buildWallDimensions(
  decomp: WallDecomposition,
  roomCenter: Vec2,
  projection: PlanProjection,
  system: MeasurementSystem,
  style: DimStyle,
  canvasBounds: SvgRect,
  occupied: SvgRect[],
  out: DimPrimitives
): void {
  const breaks = decomp.breakpoints;
  if (breaks.length < 2) return;

  const aP = decomp.wall.start;
  const bP = decomp.wall.end;
  const worldSpan = { x: bP.x - aP.x, y: bP.y - aP.y };
  const worldLen = Math.hypot(worldSpan.x, worldSpan.y);
  if (worldLen <= 0.1) return;
  const worldDir = { x: worldSpan.x / worldLen, y: worldSpan.y / worldLen };
  const aS = projection.point(aP);
  const bS = projection.point(bP);
  const dxS = bS.x - aS.x;
  const dyS = bS.y - aS.y;
  const Lscreen = Math.hypot(dxS, dyS);
  if (Lscreen <= 1) return;
  const dirS = { dx: dxS / Lscreen, dy: dyS / Lscreen };
  let nScreen = { dx: -dirS.dy, dy: dirS.dx };
  const midS = { x: (aS.x + bS.x) / 2, y: (aS.y + bS.y) / 2 };
  const centerS = projection.point(roomCenter);
  if ((centerS.x - midS.x) * nScreen.dx + (centerS.y - midS.y) * nScreen.dy > 0) {
    nScreen = { dx: -nScreen.dx, dy: -nScreen.dy }; // point away from room
  }
  const tickAngle = Math.atan2(dirS.dy, dirS.dx) + Math.PI / 4;
  let textAngle = Math.atan2(dirS.dy, dirS.dx);
  if (textAngle > Math.PI / 2) textAngle -= Math.PI;
  else if (textAngle < -Math.PI / 2) textAngle += Math.PI;

  const bpScreen: Point[] = breaks.map((u) =>
    projection.point({ x: aP.x + worldDir.x * u, y: aP.y + worldDir.y * u })
  );

  const offset = (p: Point, pts: number): Point => ({
    x: p.x + nScreen.dx * pts,
    y: p.y + nScreen.dy * pts,
  });

  const hasOpenings = breaks.length > 2;

  const addTick = (p: Point) => {
    const vx = Math.cos(tickAngle) * style.tickLength;
    const vy = Math.sin(tickAngle) * style.tickLength;
    out.ticks.push({
      x1: p.x - vx / 2,
      y1: p.y - vy / 2,
      x2: p.x + vx / 2,
      y2: p.y + vy / 2,
    });
  };

  const placeLabel = (text: string, baseCenter: Point): void => {
    const w = text.length * style.fontSize * 0.62 + 4;
    const h = style.fontSize * 1.3;
    const ca = Math.abs(Math.cos(textAngle));
    const sa = Math.abs(Math.sin(textAngle));
    const boxW = w * ca + h * sa;
    const boxH = w * sa + h * ca;
    const mkRect = (c: Point): SvgRect => ({
      x: c.x - boxW / 2,
      y: c.y - boxH / 2,
      w: boxW,
      h: boxH,
    });
    let center = baseCenter;
    let box = mkRect(center);
    let tries = 0;
    while (occupied.some((r) => rectsIntersect(r, box)) && tries < 6) {
      center = { x: center.x + nScreen.dx * style.rowPitch, y: center.y + nScreen.dy * style.rowPitch };
      box = mkRect(center);
      tries += 1;
    }
    if (occupied.some((r) => rectsIntersect(r, box))) return; // still piling up — drop it
    // Keep inside the canvas (inset 2).
    const inset: SvgRect = {
      x: canvasBounds.x + 2,
      y: canvasBounds.y + 2,
      w: canvasBounds.w - 4,
      h: canvasBounds.h - 4,
    };
    if (
      !(
        box.x >= inset.x &&
        box.y >= inset.y &&
        box.x + box.w <= inset.x + inset.w &&
        box.y + box.h <= inset.y + inset.h
      )
    ) {
      return;
    }
    occupied.push(box);
    out.labels.push({
      text,
      cx: center.x,
      cy: center.y,
      angleDeg: (textAngle * 180) / Math.PI,
    });
  };

  const drawRow = (i: number, j: number, rowOffset: number, meters: number): void => {
    if (meters <= 0.06) return;
    const p0 = offset(bpScreen[i], rowOffset);
    const p1 = offset(bpScreen[j], rowOffset);
    out.lines.push({ x1: p0.x, y1: p0.y, x2: p1.x, y2: p1.y, opacity: 0.7, width: 0.75 });
    addTick(p0);
    addTick(p1);
    const text = constructionLength(meters, system);
    const needed = text.length * style.fontSize * 0.62 + 8;
    if (Math.hypot(p1.x - p0.x, p1.y - p0.y) < needed) return;
    const mid = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
    placeLabel(text, offset(mid, style.labelLift));
  };

  // Extension lines.
  const outerOffset = hasOpenings ? style.segmentOffset + style.rowPitch : style.segmentOffset;
  for (const p of bpScreen) {
    const e0 = offset(p, style.extGap);
    const e1 = offset(p, outerOffset + style.extOvershoot);
    out.lines.push({ x1: e0.x, y1: e0.y, x2: e1.x, y2: e1.y, opacity: 0.4, width: 0.5 });
  }
  // Segment row.
  for (let k = 0; k < breaks.length - 1; k++) {
    drawRow(k, k + 1, style.segmentOffset, breaks[k + 1] - breaks[k]);
  }
  // Overall row (only with openings).
  if (hasOpenings) {
    drawRow(0, breaks.length - 1, outerOffset, breaks[breaks.length - 1] - breaks[0]);
  }
}

// MARK: - opening symbols (port of drawOpeningSymbol)

interface OpeningPrimitive {
  kind: "windowLine" | "doorLeaf" | "doorArc" | "openingDash";
  d: string; // SVG path data
}

function buildOpeningSymbol(
  slot: WallOpeningSlot,
  roomCenter: Vec2,
  projection: PlanProjection
): OpeningPrimitive[] {
  const startW = slot.startPoint;
  const endW = slot.endPoint;
  const start = projection.point(startW);
  const end = projection.point(endW);
  const out: OpeningPrimitive[] = [];

  if (slot.opening.kind === "window") {
    out.push({ kind: "windowLine", d: `M ${start.x} ${start.y} L ${end.x} ${end.y}` });
    const dir = normalize({ x: endW.x - startW.x, y: endW.y - startW.y });
    const nWorld = { x: -dir.y, y: dir.x };
    const off = 2.2 / projection.scale;
    const sP = projection.point({ x: startW.x + nWorld.x * off, y: startW.y + nWorld.y * off });
    const eP = projection.point({ x: endW.x + nWorld.x * off, y: endW.y + nWorld.y * off });
    const sM = projection.point({ x: startW.x - nWorld.x * off, y: startW.y - nWorld.y * off });
    const eM = projection.point({ x: endW.x - nWorld.x * off, y: endW.y - nWorld.y * off });
    out.push({ kind: "windowLine", d: `M ${sP.x} ${sP.y} L ${eP.x} ${eP.y}` });
    out.push({ kind: "windowLine", d: `M ${sM.x} ${sM.y} L ${eM.x} ${eM.y}` });
  } else if (slot.opening.kind === "door") {
    // Leaf + quarter-circle swing arc into the room.
    const hinge = start;
    const jamb = end;
    const radius = Math.hypot(jamb.x - hinge.x, jamb.y - hinge.y);
    if (radius <= 4) return out;
    const wallAngle = Math.atan2(jamb.y - hinge.y, jamb.x - hinge.x);
    const centerPoint = projection.point(roomCenter);
    const candidates = [wallAngle - Math.PI / 2, wallAngle + Math.PI / 2];
    let leafAngle = candidates[0];
    let bestDist = Infinity;
    for (const a of candidates) {
      const px = hinge.x + radius * Math.cos(a);
      const py = hinge.y + radius * Math.sin(a);
      const dist = Math.hypot(px - centerPoint.x, py - centerPoint.y);
      if (dist < bestDist) {
        bestDist = dist;
        leafAngle = a;
      }
    }
    // Arc from jamb to leaf tip, centered at hinge.
    const leafTip = {
      x: hinge.x + radius * Math.cos(leafAngle),
      y: hinge.y + radius * Math.sin(leafAngle),
    };
    // SVG arc: large-arc=0 (quarter), sweep depends on direction.
    const sweep = leafAngle > wallAngle ? 1 : 0;
    out.push({
      kind: "doorArc",
      d: `M ${jamb.x} ${jamb.y} A ${radius} ${radius} 0 0 ${sweep} ${leafTip.x} ${leafTip.y}`,
    });
    out.push({ kind: "doorLeaf", d: `M ${hinge.x} ${hinge.y} L ${leafTip.x} ${leafTip.y}` });
  } else {
    out.push({ kind: "openingDash", d: `M ${start.x} ${start.y} L ${end.x} ${end.y}` });
  }
  return out;
}

// MARK: - stair symbol (port of drawStairSymbol)

interface StairPrimitives {
  outline: string;
  treads: string[];
  labelAt: Point | null;
}

function buildStairSymbol(
  object: ObjectFootprint,
  projection: PlanProjection
): StairPrimitives {
  const c = object.corners; // [BL, BR, TR, TL]
  const pt = (p: Vec2) => projection.point(p);
  const outline =
    "M " + c.map((p) => `${pt(p).x} ${pt(p).y}`).join(" L ") + " Z";

  const edgeX = { x: c[1].x - c[0].x, y: c[1].y - c[0].y };
  const edgeZ = { x: c[3].x - c[0].x, y: c[3].y - c[0].y };
  const lenX = Math.hypot(edgeX.x, edgeX.y);
  const lenZ = Math.hypot(edgeZ.x, edgeZ.y);
  const runIsX = lenX >= lenZ;
  const runDir = runIsX ? edgeX : edgeZ;
  const wideDir = runIsX ? edgeZ : edgeX;
  const runLen = Math.hypot(runDir.x, runDir.y);

  const treads: string[] = [];
  if (runLen > 0.01) {
    const n = Math.max(2, Math.min(14, Math.round(runLen / 0.28)));
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const a: Vec2 = { x: c[0].x + runDir.x * t, y: c[0].y + runDir.y * t };
      const b: Vec2 = { x: a.x + wideDir.x, y: a.y + wideDir.y };
      const pa = pt(a);
      const pb = pt(b);
      treads.push(`M ${pa.x} ${pa.y} L ${pb.x} ${pb.y}`);
    }
  }
  const wideScreen = Math.hypot(wideDir.x, wideDir.y) * projection.scale;
  return {
    outline,
    treads,
    labelAt: wideScreen > 40 ? pt(object.center) : null,
  };
}

// MARK: - helpers

const normalize = (a: Vec2): Vec2 => {
  const len = Math.hypot(a.x, a.y);
  return len > 0 ? { x: a.x / len, y: a.y / len } : { x: 1, y: 0 };
};

function pathFromPolygon(poly: Vec2[], projection: PlanProjection): string {
  if (poly.length === 0) return "";
  const pts = poly.map((p) => projection.point(p));
  return "M " + pts.map((p) => `${p.x} ${p.y}`).join(" L ") + " Z";
}

// MARK: - component

export function FloorplanSvg(props: {
  geometry: ScanGeometry;
  system: MeasurementSystem;
}): JSX.Element {
  const { geometry, system } = props;

  // Default orientation squares the room and stands its longer extent vertical.
  const rotation = useMemo(
    () =>
      squareUpRotation(
        geometry.walls.map((w) => ({ start: w.start, end: w.end }))
      ),
    [geometry]
  );

  // Build all SVG primitives once per geometry/system/rotation.
  const scene = useMemo(() => {
    const projection = new PlanProjection(
      geometry.boundsMin,
      geometry.boundsMax,
      { width: VIEW_W, height: VIEW_H },
      PADDING,
      rotation
    );
    const pt = (p: Vec2) => projection.point(p);

    const roomCenter: Vec2 =
      geometry.floorPolygon.length === 0
        ? {
            x: (geometry.boundsMin.x + geometry.boundsMax.x) / 2,
            y: (geometry.boundsMin.y + geometry.boundsMax.y) / 2,
          }
        : {
            x:
              geometry.floorPolygon.reduce((a, p) => a + p.x, 0) /
              geometry.floorPolygon.length,
            y:
              geometry.floorPolygon.reduce((a, p) => a + p.y, 0) /
              geometry.floorPolygon.length,
          };

    const floorPath =
      geometry.floorPolygon.length >= 3
        ? pathFromPolygon(geometry.floorPolygon, projection)
        : "";

    const wallPaths = geometry.thickWalls.map((tw) => pathFromPolygon(tw.polygon, projection));

    const openingPrims: OpeningPrimitive[] = [];
    for (const decomp of geometry.wallDecompositions) {
      for (const slot of decomp.slots) {
        openingPrims.push(...buildOpeningSymbol(slot, roomCenter, projection));
      }
    }

    // Furniture footprints + labels; stairs get the dedicated symbol.
    const furniture: { path: string; label: string | null; labelAt: Point }[] = [];
    const stairs: StairPrimitives[] = [];
    for (const object of geometry.objects) {
      if (object.corners.length !== 4) continue;
      if (object.isStairs) {
        stairs.push(buildStairSymbol(object, projection));
        continue;
      }
      const footprintWidth =
        Math.hypot(
          object.corners[0].x - object.corners[1].x,
          object.corners[0].y - object.corners[1].y
        ) * projection.scale;
      furniture.push({
        path: pathFromPolygon(object.corners, projection),
        label: footprintWidth > 46 ? object.label : null,
        labelAt: pt(object.center),
      });
    }

    // Per-segment dimension chains with collision avoidance.
    const dims: DimPrimitives = { lines: [], ticks: [], labels: [] };
    const occupied: SvgRect[] = [];
    const canvasBounds: SvgRect = { x: 0, y: 0, w: VIEW_W, h: VIEW_H };
    for (const decomp of geometry.wallDecompositions) {
      // Skip very short walls (matches FloorplanView's `decomp.wall.length > 0.7`).
      const len = Math.hypot(
        decomp.wall.end.x - decomp.wall.start.x,
        decomp.wall.end.y - decomp.wall.start.y
      );
      if (len <= 0.7) continue;
      buildWallDimensions(
        decomp,
        roomCenter,
        projection,
        system,
        DIM_STYLE,
        canvasBounds,
        occupied,
        dims
      );
    }

    return { floorPath, wallPaths, openingPrims, furniture, stairs, dims };
  }, [geometry, system, rotation]);

  // Pan + zoom via a viewBox transform.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const isTransformed = zoom !== 1 || pan.x !== 0 || pan.y !== 0;

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const next = Math.min(8, Math.max(0.5, zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
    setZoom(next);
  };
  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    // Convert client delta to viewBox units (rough: viewBox spans VIEW_W across the element).
    const el = e.currentTarget as SVGSVGElement;
    const rect = el.getBoundingClientRect();
    const sx = VIEW_W / rect.width / zoom;
    const sy = VIEW_H / rect.height / zoom;
    setPan({ x: d.px + (e.clientX - d.x) * sx, y: d.py + (e.clientY - d.y) * sy });
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };
  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // viewBox centered, scaled by zoom, shifted by pan.
  const vbW = VIEW_W / zoom;
  const vbH = VIEW_H / zoom;
  const vbX = (VIEW_W - vbW) / 2 - pan.x;
  const vbY = (VIEW_H - vbH) / 2 - pan.y;

  return (
    <div className="relative w-full" style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}>
      <svg
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        className="h-full w-full touch-none select-none"
        style={{ background: PAPER, cursor: dragRef.current ? "grabbing" : "grab" }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={reset}
      >
        {/* Floor fill */}
        {scene.floorPath && (
          <path d={scene.floorPath} fill={ACCENT} fillOpacity={0.1} stroke="none" />
        )}

        {/* Furniture footprints */}
        {scene.furniture.map((f, i) => (
          <g key={`f${i}`}>
            <path d={f.path} fill={INK} fillOpacity={0.07} stroke={INK} strokeOpacity={0.35} strokeWidth={1} />
            {f.label && (
              <text
                x={f.labelAt.x}
                y={f.labelAt.y}
                fontSize={9}
                fontWeight={500}
                fill={INK}
                fillOpacity={0.6}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {f.label}
              </text>
            )}
          </g>
        ))}

        {/* Stairs */}
        {scene.stairs.map((s, i) => (
          <g key={`s${i}`}>
            <path d={s.outline} fill={INK} fillOpacity={0.07} stroke={INK} strokeWidth={1.5} strokeLinejoin="round" />
            {s.treads.map((d, j) => (
              <path key={j} d={d} stroke={INK} strokeOpacity={0.55} strokeWidth={0.75} fill="none" />
            ))}
            {s.labelAt && (
              <text
                x={s.labelAt.x}
                y={s.labelAt.y}
                fontSize={8}
                fontWeight={600}
                fill={INK}
                fillOpacity={0.6}
                textAnchor="middle"
                dominantBaseline="central"
              >
                STAIRS
              </text>
            )}
          </g>
        ))}

        {/* Thickened walls — poché fill + ink stroke */}
        {scene.wallPaths.map((d, i) => (
          <path
            key={`w${i}`}
            d={d}
            fill={INK}
            fillOpacity={0.12}
            stroke={INK}
            strokeWidth={1.5}
            strokeLinejoin="round"
          />
        ))}

        {/* Opening symbols (drawn inside the gaps) */}
        {scene.openingPrims.map((o, i) => {
          switch (o.kind) {
            case "windowLine":
              return <path key={`o${i}`} d={o.d} stroke={INK} strokeOpacity={0.8} strokeWidth={1} fill="none" />;
            case "doorArc":
              return (
                <path key={`o${i}`} d={o.d} stroke={INK} strokeOpacity={0.5} strokeWidth={1} fill="none" strokeDasharray="3 3" />
              );
            case "doorLeaf":
              return <path key={`o${i}`} d={o.d} stroke={INK} strokeOpacity={0.8} strokeWidth={1.5} fill="none" />;
            case "openingDash":
              return (
                <path key={`o${i}`} d={o.d} stroke={INK} strokeOpacity={0.45} strokeWidth={1.2} fill="none" strokeDasharray="4 4" />
              );
            default:
              return null;
          }
        })}

        {/* Dimension lines + ticks */}
        {scene.dims.lines.map((l, i) => (
          <line
            key={`dl${i}`}
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            stroke={INK}
            strokeOpacity={l.opacity}
            strokeWidth={l.width}
          />
        ))}
        {scene.dims.ticks.map((t, i) => (
          <line
            key={`dt${i}`}
            x1={t.x1}
            y1={t.y1}
            x2={t.x2}
            y2={t.y2}
            stroke={INK}
            strokeOpacity={0.85}
            strokeWidth={1}
          />
        ))}
        {scene.dims.labels.map((lab, i) => (
          <text
            key={`dx${i}`}
            x={0}
            y={0}
            transform={`translate(${lab.cx} ${lab.cy}) rotate(${lab.angleDeg})`}
            fontSize={DIM_STYLE.fontSize}
            fontWeight={600}
            fill={INK}
            fillOpacity={0.75}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {lab.text}
          </text>
        ))}
      </svg>

      {/* Overlay HUD */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2 text-xs">
        <span className="text-muted">Scroll to zoom · drag to pan · double-click to reset</span>
        {isTransformed ? (
          <button
            className="pointer-events-auto rounded-full bg-white/80 px-3 py-1 font-semibold text-accent shadow"
            onClick={reset}
          >
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}
